import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Fragment, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { RequestError, api } from '../../lib/api'
import { LegacyPanel } from './LegacyPanel'
import { STATUS_FA, faDate, faNum } from './labels'

/**
 * Every charity on the platform, and the one action that only exists here:
 * entering one.
 *
 * `known` is shown against the canon total rather than alone. A charity at
 * «۱۵۵ از ۱۸۵» has a populated ledger from its old evaluations; one at «۰ از
 * ۱۸۵» has never been asked anything, and those are different problems.
 */
export function OrgsTab({ canImpersonate }: { canImpersonate: boolean }) {
  const [search, setSearch] = useState('')
  const [entering, setEntering] = useState<number | null>(null)
  const [pastOf, setPastOf] = useState<number | null>(null)
  const [error, setError] = useState<string | null>(null)
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  const orgs = useQuery({
    queryKey: ['admin-orgs', search],
    queryFn: () => api.adminOrgs(search.trim() || undefined),
  })

  const enter = useMutation({
    mutationFn: ({ orgId, reason }: { orgId: number; reason: string }) =>
      api.impersonate(orgId, reason || undefined),
    onSuccess: async () => {
      /* Everything cached was the previous scope's. Clearing rather than
         invalidating is the honest move: none of it belongs to the charity we
         are now inside. */
      queryClient.clear()
      navigate('/ledger')
    },
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'ورود انجام نشد.'),
  })

  const setStatus = useMutation({
    mutationFn: ({ orgId, status }: { orgId: number; status: 'active' | 'suspended' }) =>
      api.adminSetOrgStatus(orgId, status),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin-orgs'] }),
    onError: (err) => setError(err instanceof RequestError ? err.messageFa : 'تغییر وضعیت انجام نشد.'),
  })

  return (
    <div className="stack">
      {error && <p className="notice notice--error">{error}</p>}

      <div className="card">
        <div className="field">
          <label htmlFor="org-search">جست‌وجو در مؤسسه‌ها</label>
          <input
            id="org-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="بخشی از نام مؤسسه"
          />
        </div>

        {orgs.isLoading && <p className="muted">در حال بارگذاری…</p>}
        {orgs.data && orgs.data.orgs.length === 0 && (
          <p className="muted">هیچ مؤسسه‌ای با این نام پیدا نشد.</p>
        )}

        {orgs.data && orgs.data.orgs.length > 0 && (
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr>
                  <th>مؤسسه</th>
                  <th>حساب‌ها</th>
                  <th>گزاره‌های دانسته</th>
                  <th>آخرین فعالیت</th>
                  <th>وضعیت</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {orgs.data.orgs.map((org) => (
                  <Fragment key={org.id}>
                    <tr>
                      <td>
                        <div>{org.name}</div>
                        <div className="meta">
                          {org.legacyCharityId === null
                            ? 'بدون سابقه در پرتال'
                            : `شمارهٔ پرتال ${faNum(org.legacyCharityId)}`}
                          {' · '}
                          ساخته شده {faDate(org.createdOn)}
                        </div>
                      </td>
                      <td>
                        <span className="num">{faNum(org.users)}</span>
                      </td>
                      <td>
                        <span>
                          <span className="num">{faNum(org.known)}</span> از{' '}
                          <span className="num">{faNum(185)}</span>
                        </span>
                      </td>
                      <td>{faDate(org.lastActivityOn)}</td>
                      <td>
                        <span className={org.status === 'active' ? 'badge badge--good' : 'badge badge--warn'}>
                          {STATUS_FA[org.status] ?? org.status}
                        </span>
                      </td>
                      <td>
                        <div className="table__actions">
                          <button
                            type="button"
                            className="btn btn--quiet btn--sm"
                            aria-expanded={pastOf === org.id}
                            onClick={() => setPastOf(pastOf === org.id ? null : org.id)}
                          >
                            داده‌های قبلی
                          </button>
                          {canImpersonate && org.status === 'active' && (
                            <button
                              type="button"
                              className="btn btn--ghost btn--sm"
                              onClick={() => setEntering(entering === org.id ? null : org.id)}
                            >
                              ورود به‌جای مؤسسه
                            </button>
                          )}
                          {canImpersonate && (
                            <button
                              type="button"
                              className="btn btn--quiet btn--sm"
                              onClick={() =>
                                setStatus.mutate({
                                  orgId: org.id,
                                  status: org.status === 'active' ? 'suspended' : 'active',
                                })
                              }
                            >
                              {org.status === 'active' ? 'معلق کردن' : 'فعال کردن'}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {/* The confirmation sits on its own full-width row: inside the
                        actions cell it would be a column of single words. */}
                    {pastOf === org.id && (
                      <tr>
                        <td colSpan={6}>
                          <LegacyPanel orgId={org.id} canManage={canImpersonate} onClose={() => setPastOf(null)} />
                        </td>
                      </tr>
                    )}

                    {entering === org.id && (
                      <tr>
                        <td colSpan={6}>
                          <EnterForm
                            orgName={org.name}
                            pending={enter.isPending}
                            onCancel={() => setEntering(null)}
                            onSubmit={(reason) => enter.mutate({ orgId: org.id, reason })}
                          />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * The reason is optional and recorded. It is asked for because «چرا کسی روز سوم
 * توی حساب ما بود؟» is a question a charity is entitled to a straight answer to,
 * and an empty box produces a worse answer than a one-line one.
 */
function EnterForm({
  orgName,
  pending,
  onCancel,
  onSubmit,
}: {
  orgName: string
  pending: boolean
  onCancel: () => void
  onSubmit: (reason: string) => void
}) {
  const [reason, setReason] = useState('')

  return (
    <div className="notice notice--info">
      <p>
        بعد از این، همهٔ صفحه‌ها داده‌های «{orgName}» را نشان می‌دهند. هر کاری بکنید به نام همین
        مؤسسه ثبت می‌شود و نام شما هم کنارش می‌ماند.
      </p>
      <div className="field">
        <label htmlFor="reason">برای چه کاری وارد می‌شوید؟</label>
        <input
          id="reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="مثلاً: پیگیری تماس پشتیبانی"
        />
      </div>
      <div className="row">
        <button type="button" className="btn btn--sm" disabled={pending} onClick={() => onSubmit(reason)}>
          {pending ? 'در حال ورود…' : 'وارد می‌شوم'}
        </button>
        <button type="button" className="btn btn--quiet btn--sm" onClick={onCancel}>
          بی‌خیال
        </button>
      </div>
    </div>
  )
}
